import React, { useState, useEffect } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import dashboardAPI from '../../../services/dashboardService';
import { Users, UserPlus, UserCheck } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
    </div>
);

const DetailedUsersPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;
    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    const fetchStats = async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getUserStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error("Failed to fetch user stats", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats(filters);
    }, [filters]);

    if (loading || !stats) {
        return <div className="p-8 text-center text-gray-500">Loading statistics...</div>;
    }

    // Format Data for Charts
    const userTypeData = stats.usersByType ? Object.entries(stats.usersByType).map(([key, value]) => ({
        name: key.replace('_', ' ').toLowerCase(),
        value: value
    })) : [];

    const graphData = stats.graph_data || [];

    return (
        <DetailedPageLayout
            title="User Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            {/* Top Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                <StatCard
                    title="Total Users"
                    value={stats.totalUsers}
                    icon={Users}
                    color="bg-blue-500"
                />
                <StatCard
                    title="New Users"
                    value={stats.newUsers}
                    icon={UserPlus}
                    color="bg-green-500"
                />
                <StatCard
                    title="Active Users"
                    value={stats.activeUsers}
                    icon={UserCheck}
                    color="bg-indigo-500"
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                    <CustomDonutChart
                        data={userTypeData}
                        totalValue={stats.totalUsers}
                        title="User Distribution"
                    />
                </div>
                <div className="lg:col-span-2">
                    <CustomAreaChart
                        data={graphData}
                        color="#3b82f6"
                        title="User Growth"
                    />
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <CustomBarChart
                        data={graphData}
                        title="Registration Dynamics"
                    />
                </div>
                {/* Recent Registrations Card */}
                <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                    <h3 className="text-gray-900 font-semibold mb-4">Recent Registrations</h3>
                    <div className="space-y-4 flex-1 overflow-y-auto max-h-[300px] lg:max-h-none pr-1">
                        {stats.recentUsers && stats.recentUsers.map(user => (
                            <div key={user.id} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                                <div className="min-w-0 mr-2">
                                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{user.type}</p>
                                </div>
                                <span className="text-[10px] md:text-xs text-gray-400 whitespace-nowrap">
                                    {new Date(user.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DetailedPageLayout>
    );
};

export default DetailedUsersPage;
